import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { describeControl } from './tours/tourTargets';

/**
 * Hover help for the ticket form (New Ticket and Edit Ticket). Hovering or
 * focusing a field shows what it means and how to fill it in. Fields are
 * found by their label text, so the form itself is not modified — labels
 * with help only get a data attribute that draws a small ⓘ marker.
 */

const FORM_SELECTOR = 'form.tk-form-grid';
const SHOW_DELAY_MS = 350;
const WIDTH = 320;

const HINTS = {
  domain: {
    title: 'Domain — what kind of problem is it?',
    body: [
      'App Support: an issue in one of our software products (errors, wrong data, a screen not working).',
      'Hardware: a device problem — laptop, desktop, printer, network.',
      'Software: installing or fixing internal software and tools.',
    ],
    how: 'Open the list and pick one. Clients are set to App Support automatically. Changing the domain clears the fields below it.',
  },
  priority: {
    title: 'Priority — how urgent is it?',
    body: [
      'Critical: work is stopped for many users.',
      'High: a key function is not working for you.',
      'Medium (default): something is wrong but you can keep working.',
      'Low: a minor issue or a question.',
    ],
    how: 'Pick the level that matches the impact. It sets the target resolution time (SLA) for the ticket.',
  },
  product: {
    title: 'Product — where is the problem?',
    body: [
      'The software product the issue is in. It decides which extra fields appear and which team receives the ticket.',
    ],
    how: 'Open the list and pick the product; type to search. Only products you may raise tickets for are listed. "(not accepting tickets)" means it is switched off for new tickets.',
  },
  subject: {
    title: 'Subject — a one-line summary',
    body: ['The first thing the support team sees in every ticket list.'],
    how: 'Keep it short and specific, e.g. "Unable to print receipts in Collections".',
  },
  description: {
    title: 'Description — the full details',
    body: ['Everything the team needs to understand and reproduce the problem.'],
    how: 'Say what you were doing, what happened, any error message (copy it exactly), and since when. More detail means a faster fix.',
  },
  'ticket submitted by': {
    title: 'Ticket Submitted By',
    body: ['Filled in automatically with your name and email.'],
    how: 'Nothing to enter — updates about this ticket are sent to you.',
  },
  'attachments / screenshots': {
    title: 'Attachments / Screenshots',
    body: ['Optional files that show the problem: images, PDF, Word, Excel, CSV, TXT or LOG.'],
    how: 'Click to choose one or more files; use "remove" to drop one. They upload right after the ticket is created.',
  },
};

const cleanLabel = (text) => (text || '')
  .replace(/\(loading…\)/g, '')
  .replace(/\*/g, '')
  .replace(/\s+/g, ' ')
  .trim();

function fieldOf(node) {
  const form = node?.closest?.(FORM_SELECTOR);
  if (!form) return null;
  let wrapper = node;
  while (wrapper && wrapper.parentElement !== form) wrapper = wrapper.parentElement;
  if (!wrapper) return null;
  const label = wrapper.querySelector(':scope > label, :scope > .tk-label');
  return label ? { wrapper, label } : null;
}

function hintFor({ wrapper, label }) {
  const name = cleanLabel(label.textContent);
  if (!name) return null;
  const known = HINTS[name.toLowerCase()];
  if (known) return known;
  // Product-specific field: use the admin's help text and the control type.
  const required = label.textContent.includes('*');
  const help = Array.from(wrapper.children)
    .filter((c) => c !== label && !c.matches('input, select, textarea, .tk-ss') && !c.querySelector('input, select, textarea'))
    .map((c) => c.textContent.trim())
    .filter(Boolean)
    .join(' ');
  return {
    title: `${name}${required ? ' (required)' : ' (optional)'}`,
    body: help ? [help] : [`Details about "${name}" for this product's ticket form.`],
    how: describeControl(wrapper),
  };
}

const MARKER_CSS = `
${FORM_SELECTOR} [data-help-hint]::after {
  content: 'i';
  display: inline-flex; align-items: center; justify-content: center;
  width: 13px; height: 13px; margin-left: 5px; border-radius: 50%;
  font: italic 700 9px/1 Georgia, serif; color: #2563EB; border: 1px solid #93C5FD;
  vertical-align: 1px; cursor: help;
}`;

function FieldHints() {
  const [hint, setHint] = useState(null);
  const current = useRef(null);
  const timer = useRef(null);

  // Mark labels so people can see help is available.
  useEffect(() => {
    let scheduled = false;
    const mark = () => {
      scheduled = false;
      document.querySelectorAll(`${FORM_SELECTOR} > * > label, ${FORM_SELECTOR} > * > .tk-label`).forEach((label) => {
        if (!label.hasAttribute('data-help-hint') && cleanLabel(label.textContent)) {
          label.setAttribute('data-help-hint', '');
        }
      });
    };
    const observer = new MutationObserver(() => {
      if (!scheduled) {
        scheduled = true;
        requestAnimationFrame(mark);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    mark();
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const hide = () => {
      clearTimeout(timer.current);
      current.current = null;
      setHint(null);
    };
    const showFor = (node) => {
      const field = fieldOf(node);
      if (!field) {
        if (current.current) hide();
        return;
      }
      if (current.current === field.wrapper) return;
      clearTimeout(timer.current);
      current.current = field.wrapper;
      setHint(null);
      timer.current = setTimeout(() => {
        // Don't cover an open dropdown list.
        if (field.wrapper.querySelector('.tk-ss.is-open')) return;
        const content = hintFor(field);
        if (content) setHint({ ...content, rect: field.wrapper.getBoundingClientRect() });
      }, SHOW_DELAY_MS);
    };
    const onOver = (e) => showFor(e.target);
    // Typing means they know what to enter — get out of the way until the next field.
    const onKey = () => {
      clearTimeout(timer.current);
      setHint(null);
    };
    const onFocus = (e) => showFor(e.target);
    const onClick = (e) => {
      // Opening a dropdown hides the hint so it never covers the options.
      if (e.target.closest?.('.tk-ss')) {
        clearTimeout(timer.current);
        setHint(null);
      }
    };
    document.addEventListener('mouseover', onOver);
    document.addEventListener('focusin', onFocus);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick, true);
    window.addEventListener('scroll', hide, true);
    return () => {
      clearTimeout(timer.current);
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick, true);
      window.removeEventListener('scroll', hide, true);
    };
  }, []);

  let tooltip = null;
  if (hint) {
    const { rect } = hint;
    const vw = window.innerWidth;
    const width = Math.min(WIDTH, vw - 16);
    const left = Math.min(Math.max(8, rect.left), vw - width - 8);
    const above = rect.top > 200;
    const style = above
      ? { bottom: window.innerHeight - rect.top + 8 }
      : { top: rect.bottom + 8 };
    tooltip = (
      <div
        role="tooltip"
        style={{
          position: 'fixed', left, width, ...style, zIndex: 2900, pointerEvents: 'none',
          background: '#111827', color: 'white', borderRadius: '6px', padding: '10px 12px',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.3)', fontSize: '12px', lineHeight: 1.5,
        }}
        data-testid="field-hint"
      >
        <p style={{ fontWeight: 700, margin: '0 0 4px' }}>{hint.title}</p>
        {hint.body.map((line) => (
          <p key={line} style={{ margin: '0 0 3px', color: '#E5E7EB' }}>{line}</p>
        ))}
        <p style={{ margin: '6px 0 0', color: '#BFDBFE' }}>
          <strong style={{ color: '#93C5FD' }}>How: </strong>{hint.how}
        </p>
      </div>
    );
  }

  return createPortal(
    <>
      <style>{MARKER_CSS}</style>
      {tooltip}
    </>,
    document.body,
  );
}

export default FieldHints;
